import * as Sentry from "@sentry/tanstackstart-react";

import { isAuthError } from "./auth/auth-error";
import { isNovaSubscriptionRequiredError } from "./shared/nova/access-error";
import { UserFacingError } from "./shared/user-facing-error";

/**
 * Central reporting policy for TanStack Query mutation failures. Wired into the
 * QueryClient's `MutationCache.onError` (see router.tsx) so every current and
 * future mutation is covered in one place, rather than scattered per-site
 * `captureException` calls.
 *
 * A mutation erroring means a user's actual write (save / upload / payment /
 * key-grant) hit a real error — high signal, unlike the browser-environment
 * noise we deliberately stopped forwarding from `console.error` (see
 * client.tsx). The escapes:
 *
 * - `isAuthError` is expected control flow (the retry policy refreshes the
 *   token, else we redirect to /login) — never reported.
 * - `isNovaSubscriptionRequiredError` is likewise a gate, not a failure: an
 *   unsubscribed coach starting a walled Nova agent throws so the mutation
 *   short-circuits into paywall navigation. Nothing broke and there is nothing
 *   to fix, so reporting it only buried real mutation errors (Sentry DCM-ST).
 * - A `UserFacingError` is a refusal written for the person, not a defect —
 *   the same policy `reportToSentry` applies on the server. A server function
 *   that wraps a genuine failure in one has already reported that failure, so
 *   filing it again here would only duplicate it.
 * - A mutation opts out via `meta: { silent: true }` when its failures are
 *   genuinely expected/low-signal, and sets `meta: { surface: "..." }` to tag
 *   the resulting issue for triage.
 */
export function reportMutationError(
  error: unknown,
  meta: { silent?: unknown; surface?: unknown } | undefined,
): void {
  if (isAuthError(error)) return;
  if (isNovaSubscriptionRequiredError(error)) return;
  if (error instanceof UserFacingError) return;
  if (meta?.silent === true) return;
  Sentry.captureException(error, {
    tags: {
      surface: typeof meta?.surface === "string" ? meta.surface : "mutation",
    },
  });
}
